import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Pagination } from '@heyroot/ui/primitives/Pagination';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { fetchStaff, fetchAdminRoles, queryKeys, type StaffRow } from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;
// 'deleted' is deliberately NOT a filter option. Every staff read filters
// `deleted_at IS NULL` (staff-repo.ts), so the server's listQuery enum accepts
// only active|suspended -- offering 'deleted' here sends a value that fails
// safeParse and 400s the page into its error state. `statusVariant` below still
// handles 'deleted' because a row's own status can carry it.
const STATUSES = ['active', 'suspended'] as const;

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'deleted':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function StaffListPage() {
  const navigate = useNavigate();
  const { hasPerm } = useAdminAuth();
  const canManage = hasPerm(PERMS.STAFF_MANAGE);

  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(rawSearch);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const params = useMemo(
    () => ({
      ...(search ? { q: search } : {}),
      ...(status ? { status } : {}),
      ...(role ? { role } : {}),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [search, status, role, page],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.staff(params),
    queryFn: () => fetchStaff(params),
    placeholderData: keepPreviousData,
  });
  const { data: roles } = useQuery({
    queryKey: queryKeys.adminRoles,
    queryFn: fetchAdminRoles,
  });

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-heading-2 text-primary">Staff</h1>
          <p className="text-body-sm text-secondary">Internal admin operators.</p>
        </div>
        {canManage && (
          <Button onClick={() => void navigate({ to: '/staff/new' })}>Invite operator</Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          placeholder="Search by email or name"
          aria-label="Search staff"
          className="max-w-xs"
        />
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={role}
          onValueChange={(v) => {
            setRole(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by role">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {(roles ?? []).map((r) => (
              <SelectItem key={r.roleId} value={r.roleId}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="overflow-x-auto">
          <Table>
            <TableBody>
              <SkeletonTable rows={8} cols={6} />
            </TableBody>
          </Table>
        </div>
      ) : isError ? (
        <p className="text-body-sm text-danger-text">Could not load staff. Retry in a moment.</p>
      ) : !data || data.rows.length === 0 ? (
        <EmptyState
          title="No operators match"
          description="No operators match this search. Try a different name, email or filter."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Invited</TableHeaderCell>
                  <TableHeaderCell>Last login</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((s: StaffRow) => (
                  <TableRow key={s.uid}>
                    <TableCell>
                      <Link to="/staff/$uid" params={{ uid: s.uid }}>
                        {s.displayName ?? s.email}
                      </Link>
                    </TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.role ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(s.status)}>{s.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {s.invitedAt ? new Date(s.invitedAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {s.lastLoginAt ? new Date(s.lastLoginAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}
